/*
  Countdown Timer
  Scans for elements with class "countdown-timer" and attribute data-end-datetime.
  Primary expected format: MM/DD/YYYY (e.g., 11/25/2025). The countdown ends at local 23:59:59 of that date.
  Backward compatible: also accepts ISO 8601 strings for previously saved values.
  Updates child elements with data-part="days|hours|minutes|seconds" every second.
*/

(() => {
  // Prevent multiple registrations when the script is included by several sections
  const GLOBAL_FLAG = '__countdownTimerLoaded';
  if (typeof window !== 'undefined') {
    if (window[GLOBAL_FLAG]) {
      return; // Already loaded; avoid duplicate listeners
    }
    window[GLOBAL_FLAG] = true;
  }

  const ATTR_INIT = 'countdownInit';
  const ATTR_INTERVAL = 'countdownIntervalId';

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  function parseEndDatetime(raw) {
    if (!raw || typeof raw !== 'string') return null;

    const s = raw.trim();

    // New required format: MM/DD/YYYY → end of day local time
    const mdY = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdY) {
      const month = Number(mdY[1]);
      const day = Number(mdY[2]);
      const year = Number(mdY[3]);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const d = new Date(year, month - 1, day, 23, 59, 59, 999);
        // Guard against overflow (e.g., 02/30/2025)
        if (
          d.getFullYear() === year &&
          d.getMonth() === month - 1 &&
          d.getDate() === day
        ) {
          return d;
        } else {
          return null;
        }
      }
      return null;
    }

    // Backward compatibility: ISO 8601; also allow "YYYY-MM-DD HH:mm:ss" by converting space to 'T'
    const normalized = s.replace(' ', 'T');
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  }

  function renderRemaining(el, ms) {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const isInline = el.classList.contains('countdown-timer--inline');
    // Allow per-instance override via data-compact-days
    // By default, inline timers compact days; non-inline do not.
    const compactDays = (el.dataset.compactDays !== 'false') && (isInline || el.dataset.compactDays === 'true');

    const set = (part, value) => {
      const target = el.querySelector(`[data-part="${part}"]`);
      if (!target) return;

      if (part === 'days') {
        if (compactDays) {
          const compact = value > 99 ? '99+' : pad2(value);
          target.textContent = compact;
          target.setAttribute('data-full-days', String(value));
        } else {
          // Show full days
          target.textContent = String(value);
        }
      } else {
        target.textContent = pad2(value);
      }
    };

    set('days', days);
    set('hours', hours);
    set('minutes', minutes);
    set('seconds', seconds);
  }

  function initTimer(el) {
    if (!el || el.dataset[ATTR_INIT] === 'true') return;

    const endRaw = el.getAttribute('data-end-datetime');
    const end = parseEndDatetime(endRaw);
    el.dataset[ATTR_INIT] = 'true';

    if (!end) {
      // Invalid date: render zeros and stop
      renderRemaining(el, 0);
      el.classList.add('countdown-timer--invalid');
      return;
    }

    const tick = () => {
      const now = Date.now();
      const remaining = end.getTime() - now;
      renderRemaining(el, remaining);
      if (remaining <= 0) {
        clearInterval(Number(el.dataset[ATTR_INTERVAL] || 0));
        el.classList.add('countdown-timer--ended');
        const evt = new CustomEvent('countdown:ended', { bubbles: true, detail: { element: el, end } });
        el.dispatchEvent(evt);
      }
    };

    // Initial render and schedule updates
    tick();
    const id = window.setInterval(tick, 1000);
    el.dataset[ATTR_INTERVAL] = String(id);
  }

  function initAll(root = document) {
    root.querySelectorAll('.countdown-timer[data-end-datetime]')
      .forEach(initTimer);
  }

  function cleanup(root = document) {
    root.querySelectorAll('.countdown-timer')
      .forEach(el => {
        const id = Number(el.dataset[ATTR_INTERVAL] || 0);
        if (id) clearInterval(id);
        delete el.dataset[ATTR_INTERVAL];
        delete el.dataset[ATTR_INIT];
      });
  }

  // DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Re-initialize when Shopify editor loads or re-renders sections
  document.addEventListener('shopify:section:load', (e) => {
    const container = e && e.target ? e.target : document;
    initAll(container);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const container = e && e.target ? e.target : document;
    cleanup(container);
  });

  // Re-initialize countdowns when Cart Drawer content is refreshed
  // Some themes replace the drawer's inner HTML after add-to-cart; listen for these custom events
  function getCartEventContainer(e) {
    // Prefer a provided container in detail
    if (e && e.detail && e.detail.container instanceof Element) return e.detail.container;
    // Otherwise, if dispatched on an element, use it
    if (e && e.target instanceof Element) return e.target;
    // Fallback to the drawer element
    const drawer = document.getElementById('cart-drawer');
    if (drawer) return drawer;
    // Last resort
    return document;
  }

  document.addEventListener('cart-drawer:refreshed', (e) => {
    const container = getCartEventContainer(e);
    // No cleanup here to avoid stopping timers elsewhere; just (re)initialize newly inserted nodes
    initAll(container);
  });

  // Fallback event name used by some themes/integrations
  document.addEventListener('cart:refresh', (e) => {
    const container = getCartEventContainer(e);
    initAll(container);
  });
})();
